import { NOTE_CUT, TrackerSong, TrackerSound } from "../shared/tracker";
import { Step, renderSong } from "../shared/tracker_render";

// plays a song through web audio. the song is mixed ahead of time by the same code that writes the .wav, so what you hear
// is what the exports contain, and a moving cursor can follow it. editing while it plays takes effect the next time

let context: AudioContext | null = null;
let source: AudioBufferSourceNode | null = null;
let running: { steps: Step[]; length: number; startedAt: number; loop: boolean; frame: number; onStep: (step: Step | null) => void; onEnd: () => void } | null = null;

function audio(): AudioContext {
  if (!context) context = new AudioContext();
  if (context.state === "suspended") void context.resume();
  return context;
}

function bufferFor(samples: Float32Array, rate: number): AudioBuffer {
  const buffer = audio().createBuffer(1, Math.max(1, samples.length), rate);
  buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
  return buffer;
}

export function isPlaying(): boolean {
  return Boolean(running);
}

export function stopSong(): void {
  const current = running;
  running = null;
  if (current) window.cancelAnimationFrame(current.frame);
  try {
    source?.stop();
  } catch {
    // already stopped
  }
  source = null;
  if (current) {
    current.onStep(null);
    current.onEnd();
  }
}

function stepAt(steps: Step[], time: number): Step | null {
  let low = 0;
  let high = steps.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (steps[mid].time <= time) low = mid;
    else high = mid - 1;
  }
  return steps[low] || null;
}

export interface PlayOptions {
  loop: boolean;
  // hear the sounds after snes compression
  snes: boolean;
  onStep: (step: Step | null) => void;
  onEnd: () => void;
}

export function playSong(song: TrackerSong, options: PlayOptions): void {
  stopSong();
  const done = renderSong(song, { rate: 44100, snes: options.snes });
  if (!done.steps.length || !done.samples.length) {
    options.onEnd();
    return;
  }
  const ctx = audio();
  const node = ctx.createBufferSource();
  node.buffer = bufferFor(done.samples, done.rate);
  node.loop = options.loop;
  node.connect(ctx.destination);
  source = node;
  const state = { steps: done.steps, length: done.length, startedAt: ctx.currentTime + 0.05, loop: options.loop, frame: 0, onStep: options.onStep, onEnd: options.onEnd };
  running = state;
  node.onended = () => {
    if (running === state) stopSong();
  };
  node.start(state.startedAt);
  const tick = (): void => {
    if (running !== state) return;
    const elapsed = Math.max(0, ctx.currentTime - state.startedAt);
    state.onStep(stepAt(state.steps, state.loop ? elapsed % state.length : Math.min(elapsed, state.length)));
    state.frame = window.requestAnimationFrame(tick);
  };
  tick();
}

// one key of one sound, for the piano and the sound cards
export function previewNote(sound: TrackerSound, note: number, snes: boolean): void {
  const song: TrackerSong = {
    id: "preview00",
    title: "",
    mode: "free",
    speed: 6,
    tempo: 125,
    order: [0],
    patterns: [{ rows: 16, notes: [{ r: 0, c: 0, n: note, s: 0, v: -1 }, { r: 10, c: 0, n: NOTE_CUT, s: 0, v: -1 }] }],
    sounds: [sound],
    sfx: false,
    createdAt: "",
    updatedAt: ""
  };
  const done = renderSong(song, { rate: 44100, snes });
  const ctx = audio();
  const node = ctx.createBufferSource();
  node.buffer = bufferFor(done.samples, done.rate);
  node.connect(ctx.destination);
  node.start();
}
